import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { getPartnershipInquiries, deletePartnershipInquiry, getExportUrl } from '@/lib/api';
import { formatDate, truncate } from '@/lib/utils';
import { toast } from 'sonner';
import { Trash2, Download, Mail, Phone, Building } from 'lucide-react';

const AdminPartnerships = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const fetchInquiries = async () => {
    try {
      const response = await getPartnershipInquiries();
      setInquiries(response.data);
    } catch (error) {
      console.error('Error fetching inquiries:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInquiries();
  }, []);

  const handleDelete = async () => {
    if (!itemToDelete) return;
    
    try {
      await deletePartnershipInquiry(itemToDelete.id);
      toast.success('Inquiry deleted');
      fetchInquiries();
    } catch (error) {
      toast.error('Failed to delete inquiry');
    } finally {
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const handleExport = () => {
    const token = localStorage.getItem('admin_token');
    const url = getExportUrl('partnerships');
    window.open(`${url}?token=${token}`, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-brand-blue border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-partnerships-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-brand-blue">Partnership Inquiries</h1>
          <p className="text-gray-500 mt-1">Manage partnership interest submissions</p>
        </div>
        <Button 
          onClick={handleExport}
          className="bg-brand-teal hover:bg-brand-teal/90 text-white"
          data-testid="export-btn"
        >
          <Download size={18} className="mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Table */}
      <Card className="bg-white rounded-xl">
        <CardContent className="p-0">
          {inquiries.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-gray-500">No partnership inquiries yet.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Organization</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Interest</TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inquiries.map((inquiry) => (
                  <TableRow key={inquiry.id} data-testid={`inquiry-row-${inquiry.id}`}>
                    <TableCell>
                      <div>
                        <p className="font-medium text-brand-blue flex items-center">
                          <Building size={14} className="mr-1" />
                          {inquiry.organization_name}
                        </p>
                        <p className="text-gray-400 text-xs">{inquiry.contact_person}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <p className="text-sm flex items-center text-gray-600">
                          <Mail size={14} className="mr-1" />
                          {inquiry.email}
                        </p>
                        <p className="text-sm flex items-center text-gray-600">
                          <Phone size={14} className="mr-1" />
                          {inquiry.phone}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-brand-blue border-brand-blue/30">
                        {inquiry.organization_type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-brand-orange/10 text-brand-orange">
                        {inquiry.partnership_interest}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <p className="text-gray-500 text-sm">{truncate(inquiry.message, 60)}</p>
                    </TableCell>
                    <TableCell className="text-gray-500 text-sm">
                      {formatDate(inquiry.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => {
                          setItemToDelete(inquiry);
                          setDeleteDialogOpen(true);
                        }}
                        data-testid={`delete-${inquiry.id}`}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Inquiry</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this partnership inquiry? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminPartnerships;
